(function () {
  "use strict";

  const STORAGE_KEY = "naxarly-event";
  const STORAGE_VERSION = 2;

  const defaultState = {
    availability: {
      date: "",
      time: "",
      city: "",
      label: "",
      latitude: null,
      longitude: null,
      status: "unknown"
    },
    items: [],
    customer: {
      name: "",
      phone: "",
      email: "",
      notes: ""
    },
    meta: {
      version: STORAGE_VERSION,
      createdAt: "",
      updatedAt: ""
    }
  };

  const validAvailabilityStatuses = [
    "unknown",
    "checking",
    "available",
    "unavailable"
  ];

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function normalizeAvailability(availability) {
    const source = availability || {};
    const status = typeof source.status === "string" && validAvailabilityStatuses.indexOf(source.status) !== -1 ? source.status : "unknown";

    return {
      date: typeof source.date === "string" ? source.date : "",
      time: typeof source.time === "string" ? source.time : "",
      city: typeof source.city === "string" ? source.city : "",
      label: typeof source.label === "string" ? source.label : "",
      latitude: Number.isFinite(Number(source.latitude)) ? Number(source.latitude) : null,
      longitude: Number.isFinite(Number(source.longitude)) ? Number(source.longitude) : null,
      status: status
    };
  }

  function generateItemId(item) {
    return item.id || [item.type || "service", item.service, item.package, item.variantId || ""].join("-");
  }

  function normalizeItem(item, options) {
    const source = item || {};
    const settings = options || {};
    const service = typeof source.service === "string" ? source.service : "";
    const packageName = typeof source.package === "string" ? source.package : "";
    const generatedId = generateItemId({
      service: service,
      package: packageName
    });
    const id = settings.useGeneratedId || typeof source.id !== "string" || !source.id ? generatedId : source.id;

    return {
      id: id,
      type: typeof source.type === "string" && source.type ? source.type : "service",
      service: service,
      package: packageName,
      image: typeof source.image === "string" ? source.image : "",
      duration: typeof source.duration === "string" ? source.duration : "",
      allowance: typeof source.allowance === "string" ? source.allowance : "",
      variantId: typeof source.variantId !== "undefined" ? source.variantId : null,
      fullPrice: Number.isFinite(Number(source.fullPrice)) ? Number(source.fullPrice) : 0,
      advancePrice: Number.isFinite(Number(source.advancePrice)) ? Number(source.advancePrice) : 0,
      quantity: Number.isFinite(Number(source.quantity)) ? Number(source.quantity) : 1,
      packageKey: typeof source.packageKey === "string" ? source.packageKey : "",
      stableKey: typeof source.stableKey === "string" ? source.stableKey : "",
      parentPackageStableKey: typeof source.parentPackageStableKey === "string" ? source.parentPackageStableKey : "",
      resourceKey: typeof source.resourceKey === "string" ? source.resourceKey : "",
      resourceRequirements: Array.isArray(source.resourceRequirements) ? source.resourceRequirements.map(function (requirement) {
        return {
          key: typeof requirement.key === "string" ? requirement.key : "",
          quantity: Number.isFinite(Number(requirement.quantity)) ? Number(requirement.quantity) : 1,
          bufferBefore: Number.isFinite(Number(requirement.bufferBefore)) ? Number(requirement.bufferBefore) : 0,
          bufferAfter: Number.isFinite(Number(requirement.bufferAfter)) ? Number(requirement.bufferAfter) : 0
        };
      }).filter(function (requirement) { return requirement.key; }) : [],
      minQuantity: Number.isFinite(Number(source.minQuantity)) ? Number(source.minQuantity) : 0,
      maxQuantity: Number.isFinite(Number(source.maxQuantity)) ? Number(source.maxQuantity) : 1,
      increment: Number.isFinite(Number(source.increment)) ? Number(source.increment) : 1
    };
  }

  function normalizeCustomer(customer) {
    const source = customer || {};

    return {
      name: typeof source.name === "string" ? source.name : "",
      phone: typeof source.phone === "string" ? source.phone : "",
      email: typeof source.email === "string" ? source.email : "",
      notes: typeof source.notes === "string" ? source.notes : ""
    };
  }

  function createDefaultState() {
    const timestamp = new Date().toISOString();
    const nextState = clone(defaultState);

    nextState.meta.createdAt = timestamp;
    nextState.meta.updatedAt = timestamp;

    return nextState;
  }

  function normalizeMeta(meta) {
    const source = meta || {};
    const timestamp = new Date().toISOString();

    return {
      version: Number.isFinite(Number(source.version)) ? Number(source.version) : 1,
      createdAt: typeof source.createdAt === "string" && source.createdAt ? source.createdAt : timestamp,
      updatedAt: typeof source.updatedAt === "string" ? source.updatedAt : ""
    };
  }

  function uniqueItems(items) {
    return items.filter(function (item, index, allItems) {
      return item.id && allItems.findIndex(function (candidate) {
        return candidate.id === item.id;
      }) === index;
    });
  }

  function normalizeState(state) {
    const source = state || {};

    return {
      availability: normalizeAvailability(source.availability),
      items: Array.isArray(source.items) ? uniqueItems(source.items.map(normalizeItem)) : [],
      customer: normalizeCustomer(source.customer),
      meta: normalizeMeta(source.meta)
    };
  }

  function loadState() {
    try {
      const savedState = window.localStorage.getItem(STORAGE_KEY);

      if (!savedState) {
        return createDefaultState();
      }

      const parsedState = JSON.parse(savedState);
      const normalizedState = normalizeState(parsedState);

      if (normalizedState.meta.version < STORAGE_VERSION) {
        normalizedState.items = [];
        normalizedState.meta.version = STORAGE_VERSION;
        normalizedState.meta.updatedAt = new Date().toISOString();
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalizedState));
      }

      return normalizedState;
    } catch (error) {
      return createDefaultState();
    }
  }

  function saveState() {
    state.meta.updatedAt = new Date().toISOString();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }

  let state = loadState();

  window.Naxarly = window.Naxarly || {};

  window.Naxarly.Event = {
    setAddon: function (addon) {
      return this.addService(Object.assign({}, addon, { type: "addon", service: "Add-on" }));
    },

    setAddonQuantity: function (id, quantity) {
      const current = state.items.find(function (item) { return item.id === id && item.type === "addon"; });
      if (!current) return null;
      const step = Math.max(1, Number(current.increment) || 1);
      const min = Math.max(0, Number(current.minQuantity) || 0);
      const max = Math.max(min, Number(current.maxQuantity) || 1);
      const next = Math.max(min, Math.min(max, Math.round(Number(quantity) / step) * step));
      if (next === 0) { this.removeService(id); window.dispatchEvent(new CustomEvent("naxarly:event:change", { detail: clone(state) })); return null; }
      const updated = this.updateService(id, { quantity: next });
      window.dispatchEvent(new CustomEvent("naxarly:event:change", { detail: clone(state) }));
      return updated;
    },

    /**
     * Get the complete event state.
     * @returns {Object} A deep clone of the current state.
     */
    getState: function () {
      return clone(state);
    },

    /**
     * Reset the event state to its default values.
     * @returns {Object} A deep clone of the reset state.
     */
    reset: function () {
      state = createDefaultState();
      saveState();

      return clone(state);
    },

    /**
     * Get the selected availability details.
     * @returns {Object} A deep clone of the availability state.
     */
    getAvailability: function () {
      return clone(state.availability);
    },

    /**
     * Update selected availability details.
     * @param {Object} availability Availability fields to merge into state.
     * @returns {Object} A deep clone of the updated availability state.
     */
    setAvailability: function (availability) {
      state.availability = normalizeAvailability(Object.assign({}, state.availability, availability));
      saveState();

      window.dispatchEvent(new CustomEvent("naxarly:event:change", { detail: clone(state) }));

      return clone(state.availability);
    },

    /**
     * Clear selected availability details.
     * @returns {Object} A deep clone of the cleared availability state.
     */
    clearAvailability: function () {
      state.availability = clone(defaultState.availability);
      saveState();

      window.dispatchEvent(new CustomEvent("naxarly:event:change", { detail: clone(state) }));

      return clone(state.availability);
    },

    /**
     * Get all selected items.
     * @returns {Array} A deep clone of the selected items.
     */
    getServices: function () {
      return clone(state.items);
    },

    /**
     * Add an item or update an existing item with the same generated id.
     * @param {Object} service Item selection to add or update.
     * @returns {Object|null} A deep clone of the saved item, or null without service and package.
     */
    addService: function (service) {
      const nextItem = normalizeItem(service, {
        useGeneratedId: false
      });

      if (!nextItem.id || nextItem.id === "-") {
        return null;
      }

      const existingIndex = state.items.findIndex(function (item) {
        return item.id === nextItem.id;
      });

      if (existingIndex === -1) {
        state.items.push(nextItem);
      } else {
        state.items[existingIndex] = nextItem;
      }

      saveState();

      window.dispatchEvent(new CustomEvent("naxarly:event:change", { detail: clone(state) }));

      return clone(nextItem);
    },

    /**
     * Update a selected item by id.
     * @param {string} id Selected package id.
     * @param {Object} updates Item fields to merge into the selected item.
     * @returns {Object|null} A deep clone of the updated item, or null if not found.
     */
    updateService: function (id, updates) {
      const existingIndex = state.items.findIndex(function (item) {
        return item.id === id;
      });

      if (existingIndex === -1) {
        return null;
      }

      state.items[existingIndex] = normalizeItem(Object.assign({}, state.items[existingIndex], updates, {
        id: id
      }));
      saveState();

      return clone(state.items[existingIndex]);
    },

    /**
     * Remove a selected item by id.
     * @param {string} id Selected package id.
     * @returns {boolean} True when an item was removed.
     */
    removeService: function (id) {
      const initialLength = state.items.length;

      state.items = state.items.filter(function (item) {
        return item.id !== id;
      });

      if (state.items.length === initialLength) {
        return false;
      }

      saveState();

      return true;
    },

    /**
     * Clear all selected items.
     * @returns {Array} A deep clone of the cleared items list.
     */
    clearServices: function () {
      state.items = [];
      saveState();
      window.dispatchEvent(new CustomEvent("naxarly:event:change", { detail: clone(state) }));

      return clone(state.items);
    },

    /**
     * Check whether an item has been selected.
     * @param {string} id Selected package id.
     * @returns {boolean} True when the item exists.
     */
    hasService: function (id) {
      return state.items.some(function (item) {
        return item.id === id;
      });
    },

    /**
     * Count selected items.
     * @returns {number} The number of selected items.
     */
    countItems: function () {
      return state.items.length;
    },

    /**
     * Check whether the event state has no selected items.
     * @returns {boolean} True when no items are selected.
     */
    isEmpty: function () {
      return state.items.length === 0;
    },

    /**
     * Get customer details.
     * @returns {Object} A deep clone of the customer state.
     */
    getCustomer: function () {
      return clone(state.customer);
    },

    /**
     * Update customer details.
     * @param {Object} customer Customer fields to merge into state.
     * @returns {Object} A deep clone of the updated customer state.
     */
    setCustomer: function (customer) {
      state.customer = normalizeCustomer(Object.assign({}, state.customer, customer));
      saveState();

      return clone(state.customer);
    }
  };
})();
